import { supabase } from '@/lib/supabaseClient';
import { ensureAuthenticatedSession } from '@/utils/authentication';
import {
  NotificationItem,
  NotificationSourceType,
  NOTIFICATION_SOURCE_TYPES,
} from '@/types/notifications';
import { parseNotificationId, toNotificationDate, uuidToNotificationId } from '@/utils/notifications';

interface BaseNotificationRow {
  id: number;
  type: NotificationSourceType;
  noti_id: string;
  community_id: string;
}

type NotificationDetailRow = Record<string, unknown>;

type NotificationDetail = {
  title: string;
  description: string;
  createdAt?: string | Date | null;
  createdBy?: string | null;
};

type NotificationConfig = {
  table?: string;
  select?: string;
  mapRow?: (row: NotificationDetailRow, base: BaseNotificationRow) => NotificationDetail;
  fallback: (base: BaseNotificationRow) => NotificationDetail;
  shouldInclude?: (args: {
    base: BaseNotificationRow;
    detail?: NotificationDetailRow;
    currentUserId: string | null;
  }) => boolean;
};

const NOTIFICATION_CONFIGS: Partial<Record<NotificationSourceType, NotificationConfig>> = {
  EVENT: {
    table: 'events',
    select: 'id, created_at, name, description',
    mapRow: (row) => ({
      title: (row.name as string | null) ?? 'Nuevo evento publicado',
      description:
        (row.description as string | null) ??
        'Se ha creado un nuevo evento en tu comunidad.',
      createdAt: row.created_at as string | null,
    }),
    fallback: () => ({
      title: 'Nuevo evento publicado',
      description: 'Consulta la sección de eventos para más información.',
    }),
  },
  PAYMENT: {
    fallback: () => ({
      title: 'Nuevo aviso de pago',
      description: 'Revisa la sección de pagos para conocer los detalles.',
    }),
  },
  INCIDENT: {
    table: 'incidents',
    select: 'id, created_at, title, description, status, created_by',
    mapRow: (row) => ({
      title: (row.title as string | null) ?? 'Actualización de incidencia',
      description:
        (row.description as string | null) ??
        (row.status ? `Estado actual: ${row.status}` : 'Incidencia registrada en tu comunidad.'),
      createdAt: row.created_at as string | null,
      createdBy: (row.created_by as string | null) ?? null,
    }),
    fallback: () => ({
      title: 'Incidencia actualizada',
      description: 'Se ha registrado o actualizado una incidencia.',
    }),
    shouldInclude: ({ detail, currentUserId }) => {
      if (!currentUserId) {
        return true;
      }

      const createdBy = typeof detail?.created_by === 'string' ? detail.created_by : null;
      return createdBy !== currentUserId;
    },
  },
  MEETING: {
    table: 'meetings',
    select: 'id, created_at, title, description',
    mapRow: (row) => ({
      title: (row.title as string | null) ?? 'Nueva reunión programada',
      description:
        (row.description as string | null) ??
        'Se ha programado una nueva reunión para tu comunidad.',
      createdAt: row.created_at as string | null,
    }),
    fallback: () => ({
      title: 'Nueva reunión programada',
      description: 'Consulta la sección de reuniones para más detalles.',
    }),
  },
  AGREEMENT: {
    table: 'agreements',
    select: 'id, created_at, title, description',
    mapRow: (row) => ({
      title: (row.title as string | null) ?? 'Nuevo acuerdo registrado',
      description:
        (row.description as string | null) ??
        'Se ha registrado un nuevo acuerdo en la comunidad.',
      createdAt: row.created_at as string | null,
    }),
    fallback: () => ({
      title: 'Nuevo acuerdo registrado',
      description: 'Revisa la sección de acuerdos para más información.',
    }),
  },
  NOTICE: {
    table: 'notices',
    select: 'id, created_at, title, description',
    mapRow: (row) => ({
      title: (row.title as string | null) ?? 'Nuevo anuncio publicado',
      description:
        (row.description as string | null) ??
        'Hay un nuevo aviso disponible para tu comunidad.',
      createdAt: row.created_at as string | null,
    }),
    fallback: () => ({
      title: 'Nuevo anuncio publicado',
      description: 'Consulta la sección de anuncios para conocer los detalles.',
    }),
  },
  POLL: {
    table: 'polls',
    select: 'id, created_at, title, description',
    mapRow: (row) => ({
      title: (row.title as string | null) ?? 'Nueva votación disponible',
      description:
        (row.description as string | null) ??
        'Participa en la nueva votación de tu comunidad.',
      createdAt: row.created_at as string | null,
    }),
    fallback: () => ({
      title: 'Nueva votación disponible',
      description: 'Hay una nueva votación disponible en tu comunidad.',
    }),
  },
  PROPOSAL: {
    table: 'proposals',
    select: 'id, created_at, title, description, created_by',
    mapRow: (row) => ({
      title: (row.title as string | null) ?? 'Nueva propuesta registrada',
      description:
        (row.description as string | null) ??
        'Se ha creado una nueva propuesta en tu comunidad.',
      createdAt: row.created_at as string | null,
      createdBy: (row.created_by as string | null) ?? null,
    }),
    fallback: () => ({
      title: 'Nueva propuesta registrada',
      description: 'Revisa la sección de propuestas para más información.',
    }),
    shouldInclude: ({ detail, currentUserId }) => {
      if (!currentUserId) {
        return true;
      }

      const createdBy = typeof detail?.created_by === 'string' ? detail.created_by : null;
      return createdBy !== currentUserId;
    },
  },
  DOCUMENT: {
    fallback: (base) => {
      const documentName = base.noti_id.trim();
      const hasDocumentName = documentName.length > 0;

      return {
        title: hasDocumentName
          ? `Nuevo documento: ${documentName}`
          : 'Nuevo documento disponible',
        description: hasDocumentName
          ? `Se ha añadido "${documentName}" a la documentación de tu comunidad.`
          : 'Consulta la sección de documentación para revisar el archivo.',
      };
    },
  },
  USER_NOTIFICATION: {
    fallback: () => ({
      title: 'Notificación del usuario',
      description: 'Hay una notificación pendiente para este usuario.',
    }),
  },
};

const isNotificationSourceType = (value: string): value is NotificationSourceType =>
  (NOTIFICATION_SOURCE_TYPES as readonly string[]).includes(value as NotificationSourceType);

const toCategory = (type: NotificationSourceType) => type.toLowerCase() as NotificationItem['type'];

export interface GetNotificationsOptions {
  limit?: number;
  afterId?: number | null;
  currentUserId?: string | null;
}

export async function getNotificationsByCommunity(
  communityId: string | string[],
  types: NotificationSourceType[],
  options?: GetNotificationsOptions,
): Promise<NotificationItem[]> {
  await ensureAuthenticatedSession();

  const communityIds = (Array.isArray(communityId) ? communityId : [communityId])
    .map((id) => `${id ?? ''}`.trim())
    .filter((id) => id.length > 0);

  if (communityIds.length === 0 || types.length === 0) {
    return [];
  }

  const uniqueTypes = Array.from(new Set(types));
  if (uniqueTypes.length === 0) {
    return [];
  }

  const currentUserId = typeof options?.currentUserId === 'string' ? options.currentUserId : null;
  const limit = Math.max(1, options?.limit ?? 20);

  const query = supabase
    .from('notifications')
    .select('id, type, noti_id, community_id')
    .in('type', uniqueTypes);

  if (communityIds.length === 1) {
    query.eq('community_id', communityIds[0]);
  } else {
    query.in('community_id', communityIds);
  }

  if (typeof options?.afterId === 'number' && Number.isFinite(options.afterId)) {
    query.gt('id', options.afterId);
  }

  const { data, error } = await query.order('id', { ascending: false }).limit(limit);

  if (error) {
    throw new Error(error.message || 'No se pudieron cargar las notificaciones.');
  }

  const baseRows = (data ?? [])
    .map((row) => {
      if (!row || typeof row !== 'object') {
        return null;
      }

      const record = row as Record<string, unknown>;
      const notificationId = parseNotificationId(record.id);
      const notiId = record.noti_id;
      const community = record.community_id;
      const type = record.type;

      if (
        notificationId === null ||
        typeof notiId !== 'string' ||
        typeof community !== 'string' ||
        typeof type !== 'string' ||
        !isNotificationSourceType(type)
      ) {
        return null;
      }

      return {
        id: notificationId,
        type,
        noti_id: notiId,
        community_id: community,
      } satisfies BaseNotificationRow;
    })
    .filter((row): row is BaseNotificationRow => row !== null);

  if (baseRows.length === 0) {
    return [];
  }

  const groupedIds = new Map<NotificationSourceType, string[]>();
  for (const row of baseRows) {
    const config = NOTIFICATION_CONFIGS[row.type];
    if (!config || !config.table || !config.select) {
      continue;
    }

    if (!groupedIds.has(row.type)) {
      groupedIds.set(row.type, []);
    }

    groupedIds.get(row.type)!.push(row.noti_id);
  }

  const detailsByType = new Map<NotificationSourceType, Map<string, NotificationDetailRow>>();

  await Promise.all(
    Array.from(groupedIds.entries()).map(async ([type, ids]) => {
      const config = NOTIFICATION_CONFIGS[type];
      if (!config || !config.table || !config.select) {
        detailsByType.set(type, new Map());
        return;
      }

      const uniqueIds = Array.from(new Set(ids));
      if (uniqueIds.length === 0) {
        detailsByType.set(type, new Map());
        return;
      }

      const query = supabase
        .from(config.table)
        .select(config.select)
        .in('id', uniqueIds);

      if (communityIds.length === 1) {
        query.eq('community_id', communityIds[0]);
      } else {
        query.in('community_id', communityIds);
      }

      const { data: detailRows, error: detailError } = await query;
      if (detailError) {
        throw new Error(detailError.message || 'No se pudieron obtener los detalles de la notificación.');
      }

      const mapped = new Map<string, NotificationDetailRow>();
      for (const row of detailRows ?? []) {
        if (!row || typeof row !== 'object') {
          continue;
        }

        const record = row as NotificationDetailRow;
        if (typeof record.id === 'string') {
          mapped.set(record.id, record);
        }
      }
      detailsByType.set(type, mapped);
    }),
  );

  const notifications: NotificationItem[] = [];

  for (const base of baseRows) {
    const config = NOTIFICATION_CONFIGS[base.type];
    if (!config) {
      continue;
    }
    const detail = detailsByType.get(base.type)?.get(base.noti_id);
    const shouldInclude = config.shouldInclude
      ? config.shouldInclude({ base, detail, currentUserId })
      : true;

    if (!shouldInclude) {
      continue;
    }

    const resolved = detail && config.mapRow ? config.mapRow(detail, base) : undefined;
    const fallback = config.fallback(base);

    notifications.push({
      id: base.id,
      sourceId: base.noti_id,
      communityId: base.community_id,
      sourceType: base.type,
      type: toCategory(base.type),
      title: resolved?.title ?? fallback.title,
      description: resolved?.description ?? fallback.description,
      createdAt: toNotificationDate(resolved?.createdAt ?? fallback.createdAt ?? undefined),
    });
  }

  return notifications;
}

export interface GetUserNotificationsOptions {
  limit?: number;
  communityId?: string | null;
  filterByCommunity?: boolean;
}

export async function getUserNotifications(
  userId: string,
  options?: GetUserNotificationsOptions,
): Promise<NotificationItem[]> {
  await ensureAuthenticatedSession();

  const normalizedUserId = typeof userId === 'string' ? userId.trim() : '';

  if (!normalizedUserId) {
    return [];
  }

  const limit = Math.max(1, options?.limit ?? 20);

  const query = supabase
    .from('user_notifications')
    .select('id, type, title, body, payload, deliver_at, created_at, status')
    .eq('user_id', normalizedUserId)
    .or('"read".eq.false,"read".is.null')
    .lte('deliver_at', new Date().toISOString())
    .order('deliver_at', { ascending: false, nullsFirst: false })
    .limit(limit);

  const { data, error } = await query;

  if (error) {
    throw new Error(error.message || 'No se pudieron cargar las notificaciones del usuario.');
  }

  const fallbackCommunityId =
    typeof options?.communityId === 'string' && options.communityId.trim().length > 0
      ? options.communityId.trim()
      : 'personal';

  const defaultDescriptions: Record<string, string> = {
    incident_updated: 'Se ha actualizado el estado o comentarios de tu incidencia.',
    payment_created: 'Tienes un nuevo recibo o cuota asignada en tu comunidad.',
    payment_updated: 'Se ha actualizado el estado de tu pago.',
    reservation_reminder: 'Te recordamos tu próxima reserva de zona común.',
    document_expiration_warning: 'Un documento de tu comunidad está próximo a vencer.',
    document_due_notice: 'Aviso sobre un documento comunitario.',
    support_updated: 'Hay novedades en tu ticket de soporte.',
    support_created: 'Se ha registrado un nuevo ticket de soporte.',
    security_alert: 'Se ha registrado una alerta crítica de seguridad en la plataforma.',
    gdpr_rights_request: 'Se ha recibido una nueva solicitud de ejercicio de derechos RGPD pendiente de tramitación.',
    gdpr_rights_resolved: 'Tu solicitud formal de derechos RGPD ha sido resuelta por la administración.',
    account_closure_notice: 'Un vecino ha cerrado su cuenta. Conforme al Art. 9.1.h LPH, debes notificarle por el cauce legal ordinario.',
  };

  const defaultTitles: Record<string, string> = {
    incident_updated: 'Incidencia actualizada',
    payment_created: 'Nuevo pago asignado',
    payment_updated: 'Pago actualizado',
    reservation_reminder: 'Recordatorio de reserva',
    document_expiration_warning: 'Vencimiento de documento',
    document_due_notice: 'Aviso de documento',
    support_updated: 'Actualización de soporte',
    support_created: 'Nuevo ticket de soporte',
    security_alert: 'Alerta de seguridad',
    gdpr_rights_request: 'Solicitud de derechos RGPD',
    gdpr_rights_resolved: 'Resolución de solicitud RGPD',
    account_closure_notice: 'Baja de cuenta de vecino',
  };

  const notifications: NotificationItem[] = [];

  for (const row of data ?? []) {
    if (!row || typeof row !== 'object') {
      continue;
    }

    const record = row as Record<string, unknown>;
    const id = typeof record.id === 'string' ? record.id : null;

    if (!id) {
      continue;
    }

    const notificationId = uuidToNotificationId(id) ?? null;

    if (notificationId === null) {
      continue;
    }

    const payload =
      record.payload && typeof record.payload === 'object'
        ? (record.payload as Record<string, unknown>)
        : null;

    const rawTitle = typeof record.title === 'string' ? record.title.trim() : '';
    const rawBody = typeof record.body === 'string' ? record.body.trim() : '';
    const payloadMessage =
      payload && typeof payload.message === 'string' ? payload.message.trim() : '';
    const typeLabel = typeof record.type === 'string' ? record.type.trim() : '';
    const normalizedType = typeLabel.toLowerCase();

    const payloadCommunityId =
      payload && typeof payload.community_id === 'string' ? payload.community_id.trim() : '';

    if (
      options?.filterByCommunity &&
      options.communityId &&
      payloadCommunityId &&
      payloadCommunityId !== options.communityId
    ) {
      continue;
    }

    const deliverAtIso =
      (typeof record.deliver_at === 'string' && record.deliver_at) ||
      (record.deliver_at instanceof Date ? record.deliver_at.toISOString() : null);

    const deliverAtDate = deliverAtIso ? new Date(deliverAtIso) : null;

    if (!deliverAtDate || Number.isNaN(deliverAtDate.getTime()) || deliverAtDate.getTime() > Date.now()) {
      continue;
    }

    const createdAt =
      deliverAtIso ||
      (typeof record.created_at === 'string' && record.created_at) ||
      (record.created_at instanceof Date ? record.created_at.toISOString() : null);

    const description =
      rawBody ||
      payloadMessage ||
      defaultDescriptions[normalizedType] ||
      (typeLabel ? `Aviso: ${typeLabel}.` : 'Tienes una nueva notificación.');

    const title = rawTitle || defaultTitles[normalizedType] || 'Notificación';

    notifications.push({
      id: notificationId,
      sourceId: id,
      communityId: payloadCommunityId || fallbackCommunityId,
      sourceType: 'USER_NOTIFICATION',
      type: 'user_notification',
      userNotificationType: normalizedType || null,
      title,
      description,
      createdAt: toNotificationDate(createdAt),
    });
  }

  return notifications;
}

/**
 * Alias retrocompatible para getUserNotifications.
 */
export const getFailedUserNotifications = getUserNotifications;
export type GetFailedUserNotificationsOptions = GetUserNotificationsOptions;

export async function updateUserLastNotification(
  userId: string,
  lastNotificationId: number,
) {
  await ensureAuthenticatedSession();

  const normalizedId = Number.isFinite(lastNotificationId)
    ? Math.trunc(lastNotificationId)
    : null;

  if (!normalizedId || normalizedId <= 0) {
    throw new Error('El identificador de notificación proporcionado no es válido.');
  }

  const { error } = await supabase
    .from('users')
    .update({ noti_last: normalizedId })
    .eq('id', userId);

  if (error) {
    throw new Error(error.message || 'No se pudo actualizar el estado de las notificaciones.');
  }
}

export async function markUserNotificationsAsRead(
  userId: string,
  notificationIds: string[],
): Promise<string[]> {
  await ensureAuthenticatedSession();

  const normalizedUserId = typeof userId === 'string' ? userId.trim() : '';
  if (!normalizedUserId) {
    throw new Error('No se pudo identificar al usuario actual.');
  }

  const validIds = Array.from(
    new Set(
      notificationIds
        .map((id) => (typeof id === 'string' ? id.trim() : ''))
        .filter((id) => id.length > 0),
    ),
  );

  if (validIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from('user_notifications')
    .update({ read: true })
    .eq('user_id', normalizedUserId)
    .in('id', validIds)
    .select('id, read');

  if (error) {
    throw new Error(
      error.message || 'No se pudieron actualizar las notificaciones del usuario.',
    );
  }

  const updatedIds = (data ?? [])
    .map((row) => {
      if (!row || typeof row !== 'object') {
        return null;
      }

      const record = row as Record<string, unknown>;
      const readValue = record.read;
      const isRead =
        readValue === true ||
        readValue === 'true' ||
        readValue === 't' ||
        readValue === 1;

      if (!isRead) {
        return null;
      }

      const idValue = record.id;
      return typeof idValue === 'string' && idValue.length > 0 ? idValue : null;
    })
    .filter((id): id is string => Boolean(id));

  if (updatedIds.length !== validIds.length) {
    throw new Error(
      'Algunas notificaciones no se pudieron marcar como leídas. Inténtalo de nuevo más tarde.',
    );
  }

  return updatedIds;
}
